import React from 'react';
import { Box, Card, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { TradeStateSummary } from '../utils/dashboardTradeState';

interface DashboardSummaryStripProps {
  summary: TradeStateSummary;
}

interface SummaryCardConfig {
  key: keyof TradeStateSummary;
  label: string;
  descriptor: string;
  color: string;
  dot: string;
}

const DashboardSummaryStrip: React.FC<DashboardSummaryStripProps> = ({ summary }) => {
  const theme = useTheme();

  const cards: SummaryCardConfig[] = [
    {
      key: 'in-long',
      label: 'In a long',
      descriptor: 'entry sent',
      color: theme.palette.success.main,
      dot: theme.palette.success.main,
    },
    {
      key: 'in-short',
      label: 'In a short',
      descriptor: 'entry sent',
      color: theme.palette.error.main,
      dot: theme.palette.error.main,
    },
    {
      key: 'flat',
      label: 'Flat',
      descriptor: 'waiting to align',
      color: theme.palette.warning.main,
      dot: theme.palette.warning.main,
    },
    {
      key: 'idle',
      label: 'Idle',
      descriptor: 'no signals yet',
      color: theme.palette.text.primary,
      dot: theme.palette.text.disabled,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 2,
        mb: 3,
      }}
    >
      {cards.map((card) => {
        const count = summary[card.key];
        return (
          <Card key={card.key} sx={{ p: 2.25 }}>
            <Typography
              variant="overline"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: 10,
                color: 'text.secondary',
                mb: 1,
                lineHeight: 1.2,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: card.dot,
                  boxShadow:
                    card.key === 'idle' ? 'none' : `0 0 8px ${alpha(card.dot, 0.7)}`,
                }}
              />
              {card.label}
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontFamily: theme.typography.h3.fontFamily, lineHeight: 1, color: card.color }}
            >
              {count}{' '}
              <Typography
                component="span"
                variant="body2"
                sx={{ color: 'text.secondary', fontFamily: theme.typography.body2.fontFamily }}
              >
                {card.descriptor}
              </Typography>
            </Typography>
          </Card>
        );
      })}
    </Box>
  );
};

export default DashboardSummaryStrip;
